import React, { useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Users, Plus, Minus } from 'lucide-react';
import { useBooking } from '../hooks/useBooking';
interface GuestPickerProps {
  isOpen: boolean;
  onOpen: () => void;
  onClose: () => void;
}
export function GuestPicker({
  isOpen,
  onOpen,
  onClose
}: GuestPickerProps) {
  const {
    adults,
    rooms,
    setGuests
  } = useBooking();
  const containerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);
  const updateAdults = (delta: number) => {
    const newAdults = Math.max(1, Math.min(10, adults + delta));
    setGuests(newAdults, rooms);
  };
  const updateRooms = (delta: number) => {
    const newRooms = Math.max(1, Math.min(5, rooms + delta));
    setGuests(adults, newRooms);
  };
  return <div className="relative flex-[1.2] min-w-0" ref={containerRef} data-id="element-136">
      <div className="p-3 md:px-5 hover:bg-gray-50 rounded-xl transition-colors cursor-pointer group h-full flex flex-col justify-center" onClick={onOpen} data-id="element-137">
        <label className="block text-xs font-semibold text-gray-800 mb-1 uppercase tracking-wider cursor-pointer" data-id="element-138">
          Travellers
        </label>
        <div className="flex items-center text-gray-500 group-hover:text-gray-900 min-w-0" data-id="element-139">
          <Users className="w-4 h-4 mr-2 text-luxury-blue shrink-0" data-id="element-140" />
          <span className="text-sm font-medium text-gray-900 truncate" data-id="element-141">
            {adults} {adults === 1 ? 'adult' : 'adults'}, {rooms}{' '}
            {rooms === 1 ? 'room' : 'rooms'}
          </span>
        </div>
      </div>

      <AnimatePresence data-id="element-142">
        {isOpen && <motion.div initial={{
        opacity: 0,
        y: 10,
        scale: 0.95
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: 10,
        scale: 0.95
      }} transition={{
        duration: 0.2
      }} className="absolute top-full right-0 mt-4 w-72 md:w-80 bg-white rounded-2xl shadow-float border border-gray-100 p-6 z-50" data-id="element-143">
            <div className="space-y-6" data-id="element-144">
              {/* Adults Row */}
              <div className="flex items-center justify-between" data-id="element-145">
                <div data-id="element-146">
                  <h4 className="font-semibold text-gray-900" data-id="element-147">Adults</h4>
                  <p className="text-xs text-gray-500" data-id="element-148">Ages 13 or above</p>
                </div>
                <div className="flex items-center space-x-4" data-id="element-149">
                  <button onClick={() => updateAdults(-1)} disabled={adults <= 1} className="w-8 h-8 rounded-full border border-gray-200 flex items-center justify-center text-gray-500 hover:border-gray-400 hover:text-gray-700 disabled:opacity-30 disabled:hover:border-gray-200 transition-colors" data-id="element-150">
                    <Minus className="w-4 h-4" data-id="element-151" />
                  </button>
                  <span className="w-4 text-center font-medium text-gray-900" data-id="element-152">
                    {adults}
                  </span>
                  <button onClick={() => updateAdults(1)} disabled={adults >= 10} className="w-8 h-8 rounded-full border border-gray-200 flex items-center justify-center text-gray-500 hover:border-gray-400 hover:text-gray-700 disabled:opacity-30 disabled:hover:border-gray-200 transition-colors" data-id="element-153">
                    <Plus className="w-4 h-4" data-id="element-154" />
                  </button>
                </div>
              </div>

              {/* Rooms Row */}
              <div className="flex items-center justify-between" data-id="element-155">
                <div data-id="element-156">
                  <h4 className="font-semibold text-gray-900" data-id="element-157">Rooms</h4>
                </div>
                <div className="flex items-center space-x-4" data-id="element-158">
                  <button onClick={() => updateRooms(-1)} disabled={rooms <= 1} className="w-8 h-8 rounded-full border border-gray-200 flex items-center justify-center text-gray-500 hover:border-gray-400 hover:text-gray-700 disabled:opacity-30 disabled:hover:border-gray-200 transition-colors" data-id="element-159">
                    <Minus className="w-4 h-4" data-id="element-160" />
                  </button>
                  <span className="w-4 text-center font-medium text-gray-900" data-id="element-161">
                    {rooms}
                  </span>
                  <button onClick={() => updateRooms(1)} disabled={rooms >= 5} className="w-8 h-8 rounded-full border border-gray-200 flex items-center justify-center text-gray-500 hover:border-gray-400 hover:text-gray-700 disabled:opacity-30 disabled:hover:border-gray-200 transition-colors" data-id="element-162">
                    <Plus className="w-4 h-4" data-id="element-163" />
                  </button>
                </div>
              </div>
            </div>

            <div className="mt-6 pt-4 border-t border-gray-100 flex justify-end" data-id="element-164">
              <button onClick={onClose} className="text-sm font-semibold text-luxury-blue hover:text-blue-700 transition-colors" data-id="element-165">
                Done
              </button>
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}